import { useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import type { RfxQuoteTable } from "newui/features/Rfx/Submission/types/quote.type";
import type { UploadFile } from "types/storage.types";

interface QuoteSettingsForValidation {
	allow_flexible_quote?: boolean;
	quoteFile?: UploadFile;
	quoteInstructions?: string | null;
	quoteType?: "form" | "file";
}

export interface RowError {
	name?: boolean;
	/** Column-level cell errors: columnId → true if missing */
	cells?: Record<number, boolean>;
	unit_id?: boolean;
}

interface TableErrors {
	name?: boolean;
	description?: boolean;
	rows: Record<number, RowError>;
}

interface QuoteTabValidationResult {
	/** Human-readable error messages for display */
	errorMessages: string[];
	/** True when any validation error exists (always computed) */
	hasQuoteErrors: boolean;
	/** True after user explicitly triggers validate() — controls banner visibility */
	showBanner: boolean;
	/** Reset banner flag */
	clearBanner: () => void;
	getRowErrors: (tableIndex: number) => Record<number, RowError>;
	getTableFieldError: (tableIndex: number, field: "name" | "description") => boolean;
	tableHasError: (tableIndex: number) => boolean;
	/** Call to trigger banner display — returns true if valid */
	validate: () => boolean;
}

// --- Yup schemas ---

const quoteTableSchema = Yup.object().shape({
	name: Yup.string().trim().required("Table name is required"),
	description: Yup.string().trim().required("Table description is required"),
	rows: Yup.array()
		.min(1, "At least one row is required"),
});

const settingsSchemaForm = Yup.object().shape({
	quoteType: Yup.string().oneOf(["form", "file"]).required("Quote type is required"),
});

const settingsSchemaFile = Yup.object().shape({
	quoteType: Yup.string().oneOf(["form", "file"]).required("Quote type is required"),
	quoteFile: Yup.mixed().test(
		"file_required",
		"Please upload a quote template file",
		(val: any) => {
			return val && val.file_path;
		}
	),
});

/** Extract failed field paths from a Yup ValidationError */
const getFailedPaths = (err: unknown): Set<string> => {
	const paths = new Set<string>();
	if (err instanceof Yup.ValidationError) {
		const validationErr = err as Yup.ValidationError & { inner?: Yup.ValidationError[] };
		if (validationErr.inner) {
			for (const inner of validationErr.inner) {
				if (inner.path) {
					paths.add(inner.path);
				}
			}
		}
		// Single-error case (no inner) — use top-level path
		if (paths.size === 0 && validationErr.path) {
			paths.add(validationErr.path);
		}
	}
	return paths;
};

// --- Hook ---

export const useQuoteTabValidation = (
	quoteSettings: QuoteSettingsForValidation,
	quoteTables: RfxQuoteTable[] | undefined
): QuoteTabValidationResult => {
	const [showBanner, setShowBanner] = useState(false);

	// Errors are always computed — field-level errors show immediately,
	// the summary banner only shows after validate() is called.
	const { hasQuoteErrors, tableErrorMap, errorMessages } = useMemo(() => {
		const messages: string[] = [];

		// Validate settings
		const settingsSchema = quoteSettings.quoteType === "file" ? settingsSchemaFile : settingsSchemaForm;
		let settingsValid = true;
		try {
			settingsSchema.validateSync(quoteSettings, { abortEarly: false });
		} catch (err) {
			settingsValid = false;
			if (err instanceof Yup.ValidationError) {
				const ve = err as Yup.ValidationError & { inner?: Yup.ValidationError[] };
				for (const inner of ve.inner || []) {
					if (inner.message) {
						messages.push(inner.message);
					}
				}
				if (messages.length === 0 && ve.message) {
					messages.push(ve.message);
				}
			}
		}

		// If file mode, no table validation needed
		if (quoteSettings.quoteType === "file") {
			return {
				hasQuoteErrors: !settingsValid,
				tableErrorMap: new Map<number, TableErrors>(),
				errorMessages: messages,
			};
		}

		// Validate tables
		const tables = quoteTables?.filter((qt) => qt.id != null) || [];
		const tableErrorMap = new Map<number, TableErrors>();
		let hasTableErrors = false;

		if (tables.length === 0) {
			hasTableErrors = true;
			messages.push("At least one quote table is required");
		}

		tables.forEach((table, index) => {
			const errors: TableErrors = { rows: {} };
			let tableHasAnyError = false;

			// Validate table-level fields via schema
			try {
				quoteTableSchema.validateSync(
					{ name: table.name, description: table.description, rows: table.rows || [] },
					{ abortEarly: false }
				);
			} catch (err) {
				const failedPaths = getFailedPaths(err);
				if (failedPaths.has("name")) {
					errors.name = true;
					tableHasAnyError = true;
				}
				if (failedPaths.has("description")) {
					errors.description = true;
					tableHasAnyError = true;
				}
				if (failedPaths.has("rows")) {
					tableHasAnyError = true;
				}
			}

			// Determine which columns need procurer validation:
			// - Primary description column → row.name required
			// - Unit of measurement column → row.unitId required
			// - Additional description columns → cell value required
			const sortedCols = [...(table.columns || [])].sort((a, b) => a.sortOrder - b.sortOrder);
			const primaryDescColId = sortedCols.find((c) => c.columnType === "description")?.id ?? null;
			const hasUnitColumn = sortedCols.some((c) => c.columnType === "unit_of_measurement");
			const additionalClientCols = sortedCols.filter(
				(c) => c.isClientEditable && c.id !== primaryDescColId && c.columnType !== "unit_of_measurement"
			);

			// Validate each row
			const rows = table.rows || [];
			rows.forEach((row) => {
				const rowError: RowError = {};

				// Primary description cell required (rfxInviteId === null, on the
				// primary description column). Row identity is the UUID + cells;
				// `row.name` no longer exists.
				if (primaryDescColId !== null) {
					const descCell = row.cells?.find(
						(c) =>
							c.rfxQuoteColumnId === primaryDescColId &&
							c.rfxInviteId === null,
					);
					if (!descCell?.value || !descCell.value.trim()) {
						rowError.name = true;
					}
				}

				// Unit column → row.unitId required (only if table has a unit column)
				if (hasUnitColumn && (row.unitId === null || row.unitId === undefined || row.unitId === 0)) {
					rowError.unit_id = true;
				}

				// Additional procurer-editable columns → cell value required
				if (additionalClientCols.length > 0) {
					const cellErrors: Record<number, boolean> = {};
					for (const col of additionalClientCols) {
						const cell = row.cells?.find((c) => c.rfxQuoteColumnId === col.id);
						if (!cell?.value || !cell.value.trim()) {
							cellErrors[col.id] = true;
						}
					}
					if (Object.keys(cellErrors).length > 0) {
						rowError.cells = cellErrors;
					}
				}

				if (Object.keys(rowError).length > 0) {
					errors.rows[row.id] = rowError;
					tableHasAnyError = true;
				}
			});

			if (tableHasAnyError) {
				tableErrorMap.set(index, errors);
				hasTableErrors = true;

				const label = table.name?.trim() || `Table ${index + 1}`;
				if (errors.name) {
					messages.push(`"${label}" is missing a name`);
				}
				if (errors.description) {
					messages.push(`"${label}" is missing a description`);
				}
				const rowErrorCount = Object.keys(errors.rows).length;
				if (rowErrorCount > 0) {
					messages.push(`"${label}" has ${rowErrorCount} row(s) with missing fields`);
				}
			}
		});

		return {
			hasQuoteErrors: !settingsValid || hasTableErrors,
			tableErrorMap,
			errorMessages: messages,
		};
	}, [quoteSettings, quoteTables]);

	// Field-level errors always visible (no gate) — red borders show as user edits
	const tableHasError = useCallback(
		(tableIndex: number): boolean => {
			return tableErrorMap.has(tableIndex);
		},
		[tableErrorMap]
	);

	const getTableFieldError = useCallback(
		(tableIndex: number, field: "name" | "description"): boolean => {
			return tableErrorMap.get(tableIndex)?.[field] === true;
		},
		[tableErrorMap]
	);

	const getRowErrors = useCallback(
		(tableIndex: number): Record<number, RowError> => {
			return tableErrorMap.get(tableIndex)?.rows || {};
		},
		[tableErrorMap]
	);

	const validate = useCallback((): boolean => {
		setShowBanner(true);
		return !hasQuoteErrors;
	}, [hasQuoteErrors]);

	const clearBanner = useCallback(() => {
		setShowBanner(false);
	}, []);

	return {
		hasQuoteErrors,
		errorMessages,
		showBanner,
		tableHasError,
		getTableFieldError,
		getRowErrors,
		validate,
		clearBanner,
	};
};
